import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Play, RotateCw, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { PageTabs } from '@/components/PageTabs';
import { StatusBadge } from '@/components/StatusBadge';
import { ContainerActionDialog } from '@/components/containers/ContainerActionDialog';
import { ContainerOverview } from '@/components/containers/ContainerOverview';
import { ContainerStatsTab } from '@/components/containers/ContainerStatsTab';
import { api, ApiError } from '@/lib/api';
import { containerActions, type ContainerAction } from '@/lib/containerActions';
import { usePolledData } from '@/lib/hooks/usePolledData';
import type { Container } from '@/types/container';

const POLL_MS = 10_000;
const FOLLOW_UP_MS = [2_000, 5_000];
const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'stats', label: 'Stats' },
];
const ACTION_ORDER: ContainerAction[] = ['start', 'restart', 'stop'];
const ACTION_UI = {
  start: { label: 'Start', icon: Play },
  restart: { label: 'Restart', icon: RotateCw },
  stop: { label: 'Stop', icon: Square },
} as const;

const backLink = (
  <Link to="/resources/containers" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
    Back to containers
  </Link>
);

export function ContainerDetailPage() {
  const { containerId } = useParams<{ containerId: string }>();
  // Keyed so that navigating between containers remounts polling and every tab's state.
  return <ContainerDetail key={containerId} rawId={containerId} />;
}

function ContainerDetail({ rawId }: { rawId: string | undefined }) {
  const id = rawId !== undefined && /^\d+$/.test(rawId) && Number(rawId) > 0 ? Number(rawId) : null;
  const [params, setParams] = useSearchParams();
  const [pending, setPending] = useState<ContainerAction | null>(null);
  // Set from the fetcher's async catch path; cleared by the next successful fetch.
  const [notFound, setNotFound] = useState(false);
  const { data: container, error, loading, refetch } = usePolledData<Container | null>(
    async () => {
      if (id === null) return null;
      try {
        const c = await api.containers.get(id);
        setNotFound(false);
        return c;
      } catch (e) {
        if (e instanceof ApiError && (e.status === 404 || e.code === 'NOT_FOUND')) setNotFound(true);
        throw e;
      }
    },
    id !== null && !notFound ? POLL_MS : 0,
  );

  // The action endpoints do not update the stored row; they trigger an async sync. Re-read shortly after.
  const timers = useRef<number[]>([]);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    const list = timers.current;
    return () => {
      mountedRef.current = false;
      list.forEach((t) => window.clearTimeout(t));
      list.length = 0;
    };
  }, []);
  const onActionDone = () => {
    // The dialog can finish its request after this page has unmounted; never schedule work then.
    if (!mountedRef.current) return;
    refetch();
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current.length = 0;
    FOLLOW_UP_MS.forEach((ms) => timers.current.push(window.setTimeout(refetch, ms)));
  };

  const tabParam = params.get('tab');
  const tab = TABS.some((t) => t.id === tabParam) ? (tabParam as string) : 'overview';
  const selectTab = (next: string) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'overview') p.delete('tab');
        else p.set('tab', next);
        return p;
      },
      { replace: true },
    );

  // Not found wins even when stale data is loaded (container removed while the page was open).
  if (id === null || notFound) {
    return <EmptyState title="Container not found" description="This container does not exist or was removed." action={backLink} />;
  }

  if (!container) {
    if (loading) {
      return (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      );
    }
    return (
      <EmptyState
        title="Could not load container"
        description={error ?? undefined}
        action={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={refetch}>
              Retry
            </Button>
            {backLink}
          </div>
        }
      />
    );
  }

  const available = ACTION_ORDER.filter((a) => containerActions(container.status).includes(a));

  return (
    <div className="space-y-6">
      <PageHeader
        title={container.name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge status={container.status} />
            <span className="font-mono text-xs [overflow-wrap:anywhere]">{container.image}</span>
          </span>
        }
        actions={
          <>
            {available.map((a) => {
              const { label, icon: Icon } = ACTION_UI[a];
              return (
                <Button key={a} variant={a === 'stop' ? 'destructive' : 'outline'} size="sm" onClick={() => setPending(a)}>
                  <Icon aria-hidden />
                  {label}
                </Button>
              );
            })}
            {backLink}
          </>
        }
      >
        <PageTabs tabs={TABS} active={tab} onSelect={selectTab} label="Container sections" />
      </PageHeader>

      {error !== null ? (
        <div
          role="status"
          className="flex items-center justify-between gap-2 rounded bg-status-warning-soft px-2 py-1 text-xs text-status-warning"
        >
          <span>Showing last known data — refresh failed.</span>
          <Button variant="ghost" size="xs" onClick={refetch}>
            Retry
          </Button>
        </div>
      ) : null}

      {tab === 'overview' && <ContainerOverview container={container} />}
      {tab === 'stats' && <ContainerStatsTab containerId={container.id} status={container.status} />}

      <ContainerActionDialog container={container} action={pending} onClose={() => setPending(null)} onChanged={onActionDone} />
    </div>
  );
}
